import React, { FC, useRef, useState } from 'react';
import Image from 'components/image/Image';
import { JobCardActionEnum } from 'core/enum/jobCardAction';
import { getDateFormatter } from 'helpers/dateConverter';
import { useTranslation } from 'react-i18next';

import './Index.scss';
import { useNavigate } from 'react-router';
import { DynamicActionButton } from 'components/newButtons/DynamicActionButton';
import StatusIcon from 'components/newButtons/StatusIcon';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { IndustryList } from 'components/IndustryList';
import { clampText } from 'helpers/sharedFunctions';

const actions = [
  JobCardActionEnum.ACCEPT,
  JobCardActionEnum.CANCEL,
  JobCardActionEnum.MORE_INFO,
  JobCardActionEnum.PROFILE_VIEW
];

const OutgoingRequests: FC<any> = ({
  id,
  name,
  sub_title,
  email,
  icon,
  score,
  culture_score,
  date,
  responded_at,
  industry,
  stageOutgoingHandler,
  status,
  setOpen,
  candidateId,
  jobTitle
}): JSX.Element => {
  const navigate = useNavigate();
  const [showIndustries, setShowIndustries] = useState(false);
  const industriesBtnRef = useRef<HTMLSpanElement>(null);
  const { t } = useTranslation();

  // Get user from redux
  const user = useSelector((state: RootState) => state.user.user);
  const isRecruiterOrAdmin =
    user?.role === 'recruiter' || user?.role === 'admin';

  const canViewProfile =
    isRecruiterOrAdmin &&
    Array.isArray(user?.can_view_candidates) &&
    user.can_view_candidates.includes(Number(candidateId));

  // Withdraw handler for recruiter: only send {status: 4}
  const handleWithdraw = () => {
    if (isRecruiterOrAdmin) {
      stageOutgoingHandler(id, { status: 4 });
    } else {
      stageOutgoingHandler(id, {
        status: 4,
        responded_at: getDateFormatter(
          new Date(),
          'YYYY-MM-DDTHH:mm:ss.000000Z'
        )
      });
    }
  };

  return (
    <div className="ORCard">
      <div className="ORCard__left">
        <div onClick={() => setOpen(id, 'outgoing-requests')}>
          <Image
            url={icon}
            name={!icon ? 'no-avatar.svg' : ''}
            className="ORCard__left-image"
            objectFit="cover"
          />
        </div>
        <div className="ORCard__left__info">
          <div className="ORCard__left__info_name">
            <div className="ORCard__left__info-name-container">
              <span className="ORCard__left__info-title">{name || email}</span>
              <span className="ORCard__left__info-job-title">{jobTitle}</span>
            </div>
            {industry && (
              <IndustryList
                industry={industry}
                showIndustries={showIndustries}
                setShowIndustries={setShowIndustries}
                industriesBtnRef={industriesBtnRef}
              />
            )}
          </div>
          <span className="ORCard__left__info-sub">
            {clampText(sub_title, 45)}
          </span>
        </div>
      </div>
      <div className="ORCard__middle">
        <div className="ORCard__middle__percents">
          <span className="ORCard__middle__matches-title">
            {' '}
            {score || 0}% {t('Talent Match')}
          </span>
          <span className="ORCard__middle__matches-title">
            {culture_score || 0}% {t('Cultural Fit')}
          </span>
        </div>
      </div>
      <div className="ORCard__middle">
        <div className="ORCard__middle__percents"></div>
        <div className="ORCard__middle__details">
          <div className="ORCard__middle__details__item">
            <span className="ORCard__middle__details__item-title">
              {/* Recruiter: Change label */}
              {isRecruiterOrAdmin ? t('Request sent on') : t('Message send on')}
            </span>
            <span className="ORCard__middle__details__item-desc">
              {getDateFormatter(date, 'MMM DD YYYY, h:mm a')}
            </span>
          </div>
          {/* Only show Response on if not cancelled and recruiter */}
          {isRecruiterOrAdmin && status !== 4 && responded_at && (
            <div className="ORCard__middle__details__item">
              <span className="ORCard__middle__details__item-title">
                {t('Response on:')}
              </span>
              <span className="ORCard__middle__details__item-desc">
                {getDateFormatter(responded_at, 'MMM DD YYYY, h:mm a')}
              </span>
            </div>
          )}
          {/* For non-recruiter, keep old logic */}
          {!isRecruiterOrAdmin && responded_at && (
            <div className="ORCard__middle__details__item">
              <span className="ORCard__middle__details__item-title">
                {t('Response on:')}
              </span>
              <span className="ORCard__middle__details__item-desc">
                {getDateFormatter(responded_at, 'MMM DD YYYY, h:mm a')}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="ORCard__right">
        {/* Recruiter-specific actions */}
        {isRecruiterOrAdmin ? (
          <div className="ORCard__right__actions">
            {/* Always show More Info */}
            <DynamicActionButton
              label={t('More Info')}
              backgroundColor="#BEA0E5"
              onClick={() => setOpen(id, 'outgoing-requests')}
            />
            {/* Show Withdraw if not cancelled or accepted */}
            {status !== 4 && status !== 2 && (
              <DynamicActionButton
                label={t('Withdraw')}
                backgroundColor="red"
                onClick={handleWithdraw}
              />
            )}
            {/* Show Profile if recruiter can view */}
            {canViewProfile && (
              <DynamicActionButton
                label={t('Profile')}
                backgroundColor="#A0D5E5"
                onClick={() => navigate(`/view-profile/${candidateId}`)}
              />
            )}
            {/* Show status icon: accepted, rejected (cancelled), or no-response */}
            {status === 2 && <StatusIcon status="accepted" />}
            {status === 4 && <StatusIcon status="rejected" />} {/* Cancelled */}
            {status !== 2 && status !== 4 && (
              <StatusIcon status="no-response" />
            )}
          </div>
        ) : responded_at ? (
          <div className="ORCard__right__actions">
            {status === 2 && (
              <DynamicActionButton
                label={t('Profile')}
                backgroundColor="#A0D5E5"
                onClick={() => navigate(`/view-profile/${candidateId}`)}
              />
            )}
            {status === 2 && <StatusIcon status="accepted" />}
            {status === 3 && <StatusIcon status="rejected" />}
            {status !== 2 && status !== 4 && (
              <StatusIcon status="no-response" />
            )}
          </div>
        ) : (
          <div className="ORCard__right__actions">
            <DynamicActionButton
              label={t('More Info')}
              backgroundColor="#BEA0E5"
              onClick={() => setOpen(id, 'outgoing-requests')}
            />
            {status !== 4 && status !== 2 && (
              <DynamicActionButton
                label={t('Withdraw')}
                backgroundColor="red"
                onClick={handleWithdraw}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OutgoingRequests;
